/**
 * Resident formatting helpers shared by ResidentTable (frontend split epic,
 * Phase 2 Task 2.2 — ResidentTable has a confirmed cross-app consumer:
 * ResidentsPage.tsx AND pages/audit/FacilityAuditPage.tsx both render it).
 *
 * Extracted from frontend/src/utils/formatters.ts, logic unchanged — only the
 * `Resident` type narrowed from the full `Database["public"]["Tables"]
 * ["residents"]["Row"]` (frontend's 5000+-line generated Supabase schema,
 * which this package must not depend on) down to the fields these functions
 * actually read. Every current caller's full Resident row already satisfies
 * this narrower shape structurally, so nothing at any call site changes.
 */
import { parseDisplayDate } from "@livingsyncs/types";

export interface ResidentSummary {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  room_number: string | null;
  bed_identifier: string | null;
  dnr_status: boolean | null;
  allergies: string | null;
  mobility_level: string | null;
  diagnoses: string | null;
  photo_url: string | null;
  status: string | null;
}

/** Calculate age from date of birth. */
export function calculateAge(dateOfBirth: string | Date): number {
  const today = new Date();
  // parseDisplayDate, not `new Date`: a date-only DOB parsed as UTC midnight is
  // the PREVIOUS day locally, which made the same-month/day comparison below
  // fire 24 hours early and age everyone a day before their birthday.
  const birthDate = parseDisplayDate(dateOfBirth) ?? new Date(NaN);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

/** Get critical medical alerts for a resident. */
export interface CriticalAlerts {
  hasDNR: boolean;
  hasAllergies: boolean;
  isBedbound: boolean;
  usesWheelchair: boolean;
  hasDiagnoses: boolean;
  hasCriticalAlerts: boolean;
  allergiesText?: string;
  diagnosesText?: string;
}

export function getCriticalAlerts(resident: ResidentSummary): CriticalAlerts {
  const hasDNR = resident.dnr_status || false;
  const hasAllergies = !!resident.allergies;
  const isBedbound = resident.mobility_level === "bedbound";
  const usesWheelchair = resident.mobility_level === "wheelchair";
  const hasDiagnoses = !!resident.diagnoses;

  return {
    hasDNR,
    hasAllergies,
    isBedbound,
    usesWheelchair,
    hasDiagnoses,
    hasCriticalAlerts: hasDNR || hasAllergies || isBedbound,
    allergiesText: resident.allergies || undefined,
    diagnosesText: resident.diagnoses || undefined,
  };
}

/** Format resident room and bed information. */
export function formatRoomInfo(resident: ResidentSummary): string {
  if (!resident.room_number) return "—";

  let roomInfo = resident.room_number;
  if (resident.bed_identifier) {
    roomInfo += ` (${resident.bed_identifier})`;
  }
  return roomInfo;
}

/** Format resident full name. */
export function formatResidentName(resident: ResidentSummary): string {
  return `${resident.first_name} ${resident.last_name}`;
}
